import { useState } from "react";
import { AuthImg } from "@/components/AuthImg";
import { Input } from "@/components/ui/input";
import { draftAct } from "@/components/sales/draft/draftApi";
import { ArrowUp, ArrowDown, X, Plus, Info } from "lucide-react";

// THE PUBLIC PHOTOS — a separate list from the item's own photos, in order, each with its own words. Only a photo
// picked here can ever cross; teardown photos, receipts and every photo not picked never do.
export function DraftPhotos({ v, onView }) {
  const [words, setWords] = useState({});
  const [busy, setBusy] = useState(false);
  const list = v.photos || [];
  const put = async (photos) => { if (busy) return; setBusy(true); await draftAct(v, "put", "/photos", { photos, base: v.photos_rev }, onView); setBusy(false); };
  const move = (i, d) => { const x = [...list]; [x[i], x[i + d]] = [x[i + d], x[i]]; put(x); };
  const free = (v.item_photos || []).filter((p) => !list.some((x) => x.file_id === p.file_id));
  const can = v.can.prepare;
  return (
    <section data-testid="sales-draft-photos" className="rounded-xl border border-slate-200 bg-white p-3 space-y-3">
      <div className="font-bold text-slate-900">Public photos <span className="font-mono text-xs text-slate-500">{list.length}</span></div>
      <p className="text-xs text-slate-600 inline-flex gap-1.5"><Info className="w-4 h-4 shrink-0" />Pick photos of the {v.kind} itself — no tags, plates or VIN stickers of the truck in the frame. Say in a few words what a buyer sees.</p>
      {list.map((p, i) => (
        <div key={p.file_id} data-testid={`sales-draft-photo-${i}`} className="flex gap-3 items-start">
          <AuthImg fileId={p.file_id} thumb alt="" className="w-20 h-20 rounded-lg object-cover bg-slate-100 shrink-0" />
          <div className="flex-1 min-w-0 space-y-1.5">
            <Input data-testid={`sales-draft-photo-${i}-words`} disabled={!can} placeholder="What a buyer sees, e.g. Front, both hinges" className="h-11"
              value={words[p.file_id] ?? p.description ?? ""} onChange={(e) => setWords({ ...words, [p.file_id]: e.target.value })}
              onBlur={() => words[p.file_id] !== undefined && words[p.file_id] !== p.description && put(list.map((x) => (x.file_id === p.file_id ? { ...x, description: words[p.file_id] } : x)))} />
            {can && <div className="flex gap-1.5">
              <Btn testid={`sales-draft-photo-${i}-up`} disabled={busy || i === 0} onClick={() => move(i, -1)}><ArrowUp className="w-4 h-4" /></Btn>
              <Btn testid={`sales-draft-photo-${i}-down`} disabled={busy || i === list.length - 1} onClick={() => move(i, 1)}><ArrowDown className="w-4 h-4" /></Btn>
              <Btn testid={`sales-draft-photo-${i}-off`} disabled={busy} onClick={() => put(list.filter((x) => x.file_id !== p.file_id))}><X className="w-4 h-4" />Take off</Btn>
            </div>}
          </div>
        </div>
      ))}
      {can && free.length > 0 && (
        <div data-testid="sales-draft-photo-pick" className="flex flex-wrap gap-2 pt-1">
          {free.map((p) => (
            <button key={p.file_id} type="button" disabled={busy} data-testid={`sales-draft-photo-add-${p.file_id}`} onClick={() => put([...list, { file_id: p.file_id, description: "" }])}
              className="relative rounded-lg border-2 border-dashed border-slate-300 hover:border-slate-500 p-0.5 disabled:opacity-40">
              <AuthImg fileId={p.file_id} thumb alt="" className="w-16 h-16 rounded-md object-cover bg-slate-100" />
              <span className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-[#1F1F1F] text-white grid place-items-center"><Plus className="w-3.5 h-3.5" /></span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

function Btn({ testid, onClick, disabled, children }) {
  return <button type="button" data-testid={testid} disabled={disabled} onClick={onClick}
    className="inline-flex items-center gap-1 h-11 min-w-11 px-3 rounded-lg border-2 border-slate-300 bg-white text-sm font-bold hover:bg-slate-50 disabled:opacity-40">{children}</button>;
}
